import {EventEmitter} from "./EventEmitter";
import {is_array} from "../util/types";
import {createLogger} from "../util/logger";
import {getParent} from "../util/dom";

const console = createLogger("Component");

export class Component extends EventEmitter {

    static name;

    get name() {
        return Component.name;
    }

    constructor(el) {
        super();


        /**
         * @type {HTMLElement} the root element
         */
        this.el = el;

        /**
         * Maps elements to events and listeners
         * @type {Map<HTMLElement, Map<string, function>>}
         */
        this.eventListeners = new Map();

        const attributeName = `data-${this.name}`;


        /**
         * List of html elements bound by data attributes
         * @type {{[elementName: string]: HTMLElement|HTMLElement[]}}
         */
        this.$elements = this.querySelectorAll(`[${attributeName}]`).reduce((result, element) => {
            const name = element.getAttribute(attributeName);
            if (!name) return result;

            // If an element with same name was already queried
            if (result[name]) {
                // convert it to array if not already an array
                if (!is_array(result[name])) {
                    result[name] = [result[name]];
                }
                // Add element to list of elements
                result[name].push(element);
            } else {
                // Otherwise set single element
                result[name] = element;
            }
            return result;
        }, {});

    }

    /**
     * Adds event listener for {event} to {element}
     * @param {HTMLElement|HTMLElement[]} element The element to attach listener to
     * @param {string} event Event name to listen on
     * @param {function} listener Callback to execute when event fires
     * @param {bool|AddEventListenerOptions|undefined} options Optional event listener options
     */
    addEventListener(element, event, listener, options = undefined) {

        // Return if no elements were passed
        if (!element || (is_array(element) && !element?.length)) return;

        if (!is_array(element)) {
            element = [element];
        }

        if (!this.eventListeners) this.eventListeners = new Map();

        element.forEach((el) => {
            if (!this.eventListeners.has(el)) {
                this.eventListeners.set(el, new Map());
            }

            el.addEventListener(event, listener, options);

            this.eventListeners.get(el).set(event, listener);
        });
    }

    removeEventListener(element, event, options = undefined) {
        if (!is_array(element)) {
            element = [element];
        }

        if (!this.eventListeners) this.eventListeners = new Map();

        element.forEach((el) => {
            const _listener = this.eventListeners.get(el)?.get(event);
            if (!this.eventListeners.has(el)) {
                return;
            }

            el.removeEventListener(event, _listener, options);
            this.eventListeners.get(el).delete(event);
        });
    }

    destroy() {
        this.eventListeners.forEach((listeners, element) => {
            listeners.forEach((listener, event) => {
                element.removeEventListener(event, listener);
            });
        });
    }

    /**
     * Queries elements by data name
     * @param {string} name
     * @return {HTMLElement|HTMLElement[]|null}
     */
    $(name) {
        const elements = this.querySelectorAll(this.getAttributeSelector(name));

        if (!elements.length) return null;
        if (elements.length === 1) return elements[0];

        return Array.from(elements);
    }

    /**
     * querySelector on component root
     * @param {string} selector
     * @returns {HTMLElement|null}
     */
    querySelector(selector) {
        return this.el.querySelector(selector);
    }

    /**
     * querySelectorAll on component root
     * @param {string} selector
     * @returns {HTMLElement[]}
     */
    querySelectorAll(selector) {
        return Array.from(this.el.querySelectorAll(selector));
    }

    /**
     * Returns component attribute for name
     * Example:
     * getAttributeString("name"); // data-${this.name}="name"
     * @param {string|undefined} name
     */
    getAttributeString(name) {
        let attr = `data-${this.name}`;
        if (name) {
            attr += `="${name}"`;
        }
        return attr;
    }

    /**
     * Retuurns attribute name for attr {name} prefixed with the components name
     * Example:
     * getComponentAttributeName("custom-attr") // "data-component-name-custom-attr"
     * @param name
     * @return {*}
     */
    getComponentAttributeName(name) {
        return `data-${this.name}-${name}`;
    }

    /**
     * Returns component attribute selector for name
     * Example:
     * getAttributeSelector("name"); // [data-${this.name}="name"]
     * @param {string|undefined} name The value of the attribute. If undefined only attribute will be selected
     */
    getAttributeSelector(name) {
        return `[${this.getAttributeString(name)}]`;
    }

    /**
     * Returns prefixed classname in BEM convention
     * @param className
     */
    getClassName(className) {
        return `${this.name}${className.startsWith("--") ? "" : "__"}${className}`;
    }

    /**
     * Dispatches dom event on module element
     *
     * @param {string} event event name
     * @param {object} detail Optional event detail
     */
    dispatchDomEvent(event, detail = {}) {
        console.log("dispatchDomEvent", event, detail);
        this.el.dispatchEvent(new CustomEvent(event, {
            detail: {
                ...detail,
                el: this.el,
                component: this,
            },
            bubbles: true,
            cancelable: false,
        }))
    }

    /**
     * @param el
     * @param {boolean} recreate Pass true to recreate component
     * @return {self}
     */
    static create(el, recreate = false) {
        let instance;

        if (el.hasOwnProperty(this.name)) {
            instance = el[this.name];

            // When forcing recreation destroy old instance and return new
            if (recreate) {
                instance?.destroy();
            }
            else { // otherwise return existing instance
                return instance;
            }
        }

        el[this.name] = new this(el);

        return el[this.name];
    }

    static getInstance(el, componentName) {
        name = componentName || this.name;
        return el.hasOwnProperty(name) ? el[name] : null;
    }

    /**
     * Returns module selector for the component class
     * @param {string|undefined} name Optional name of component to get selector for, defaults to {this.name}
     * @returns {string}
     */
    static getModuleSelector(name) {
        name = name || this.name;
        return `[data-module-${name}]`;
    }

    /**
     *
     * @param el
     * @param {string} component
     * @return {HTMLElement}
     */
    static findComponentElementInTree(el, component) {
        const selector = Component.getModuleSelector(component);
        console.log("selector", selector);
        return getParent(el, selector);
    }

    /**
     *
     * @param el
     * @param {typeof Component} component
     * @return {Component|null} component instance or null if it doesnt exist
     */
    static findComponentInTree(el, component) {
        const componentEl = this.findComponentElementInTree(el, component.name);
        console.log("findComponentInTree", componentEl);

        if (componentEl) {
            console.log("getInstance", componentEl, component.name);
            return component.getInstance(componentEl, component.name);
        }

        return null;
    }

}
